import React, { useState, useEffect, useCallback } from 'react';
import { FiShare2, FiX, FiCheck } from 'react-icons/fi';
import { fetchGroups, publishPlan, fetchPlanPublicationStatus } from '../api';

const PublishDialog = ({ 
  isOpen, 
  onClose, 
  planId, 
  planName, 
  user,
  onSuccess 
}) => {
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);

  const loadGroupsAndStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Gruppen laden
      const groupsResult = await fetchGroups(user.username);
      if (groupsResult.error) {
        setError('Fehler beim Laden der Gruppen: ' + groupsResult.error);
        return;
      }
      
      setGroups(groupsResult.groups || []);
      
      // Aktuellen Veröffentlichungs-Status laden
      const statusResult = await fetchPlanPublicationStatus(planId, user.username);
      if (statusResult.success) {
        const publishedGroupIds = statusResult.publishedGroups.map(g => g.id);
        setSelectedGroups(publishedGroupIds);
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [planId, user.username]);

  // Gruppen und aktuellen Status laden
  useEffect(() => {
    if (isOpen && planId && user?.username) {
      loadGroupsAndStatus();
    }
  }, [isOpen, planId, user?.username, loadGroupsAndStatus]);

  const handleGroupToggle = (groupId) => {
    setSelectedGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  const handlePublish = async () => {
    setPublishing(true);
    setError(null);
    
    try {
      const result = await publishPlan(planId, selectedGroups, user.username);
      
      if (result.error) {
        setError('Fehler beim Veröffentlichen: ' + result.error);
      } else if (result.success) {
        onSuccess && onSuccess();
        onClose();
      } else {
        setError('Unerwartete Antwort vom Server');
      }
    } catch (err) {
      setError('Verbindungsfehler beim Veröffentlichen');
    } finally {
      setPublishing(false);
    }
  };

  const handleRevokeAll = async () => {
    setPublishing(true);
    setError(null);
    
    try {
      // Leere Gruppen-Liste senden um alle Veröffentlichungen zu entziehen
      const result = await publishPlan(planId, [], user.username);
      
      if (result.error) {
        setError('Fehler beim Entziehen der Veröffentlichung: ' + result.error);
      } else if (result.success) {
        onSuccess && onSuccess();
        onClose();
      } else {
        setError('Unerwartete Antwort vom Server');
      }
    } catch (err) {
      setError('Verbindungsfehler beim Entziehen der Veröffentlichung');
    } finally {
      setPublishing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        minWidth: '400px',
        maxWidth: '500px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '18px',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FiShare2 />
            Plan veröffentlichen / entziehen
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#666',
              padding: '4px'
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <p style={{
            margin: '0 0 8px 0',
            fontSize: '14px',
            color: '#666'
          }}>
            Plan: <strong>{planName}</strong>
          </p>
          <p style={{
            margin: '0 0 16px 0',
            fontSize: '14px',
            color: '#666'
          }}>
            Wählen Sie die Gruppen aus, für die dieser Plan verfügbar sein soll:
          </p>
        </div>

        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            color: '#666'
          }}>
            Lade Gruppen...
          </div>
        )}

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#ffebee',
            color: '#d32f2f',
            borderRadius: '4px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <div style={{ marginBottom: '20px' }}>
              {groups.map(group => (
                <label
                  key={group.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 0',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(group.id)}
                    onChange={() => handleGroupToggle(group.id)}
                    style={{
                      width: '16px',
                      height: '16px'
                    }}
                  />
                  <span style={{ color: '#333' }}>
                    {group.name}
                  </span>
                  {group.description && (
                    <span style={{ color: '#999', fontSize: '12px' }}>
                      - {group.description}
                    </span>
                  )}
                </label>
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: selectedGroups.length > 0 ? 'space-between' : 'flex-end'
            }}>
              {/* Entziehen-Button nur anzeigen wenn aktuell Gruppen ausgewählt sind */}
              {selectedGroups.length > 0 && (
                <button
                  onClick={handleRevokeAll}
                  disabled={publishing}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    backgroundColor: publishing ? '#ccc' : '#f44336',
                    color: 'white',
                    borderRadius: '4px',
                    cursor: publishing ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  title="Alle Veröffentlichungen für diesen Plan entfernen"
                >
                  <FiX />
                  Veröffentlichung komplett entziehen
                </button>
              )}
              
              <div style={{
                display: 'flex',
                gap: '12px'
              }}>
                <button
                  onClick={onClose}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    backgroundColor: 'white',
                    color: '#666',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Abbrechen
                </button>
                <button
                  onClick={handlePublish}
                  disabled={publishing}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    backgroundColor: publishing ? '#ccc' : '#4caf50',
                    color: 'white',
                    borderRadius: '4px',
                    cursor: publishing ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {publishing ? (
                    <>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        border: '2px solid white',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Veröffentliche...
                    </>
                  ) : (
                    <>
                      <FiCheck />
                      Veröffentlichen
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublishDialog;